import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { validateEmail } from '../../util';
import { useAppContext } from '../../context/AppContext';
import { useNotification } from '../../components/notifications/useNotification';
import FormButton from '../../components/FormButton/FormButton';
import FormField from '../../components/FormField/FormField';
import axios from 'axios';
import useDocumentMeta from '../../utils/useDocumentMeta';
import PublicNavbar from '../../components/PublicNavbar/PublicNavbar';
import './LoginPage.css';

const LoginPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setDietist, currentLanguage, dietist } = useAppContext();
    const notification = useNotification();
    useDocumentMeta('seo-login-title', 'seo-login-desc', { canonicalPath: '/login' });

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        keepLogin: false,
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    /* ── Auto-login if a valid session cookie is present ── */
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const { data } = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/auth/verify-token`,
                    { params: { verifyOnly: true }, withCredentials: true }
                );
                // Restore dietist context if localStorage was cleared
                if (!dietist && data.userId) {
                    const profileRes = await axios.get(
                        `${process.env.REACT_APP_BACKEND_URL}/dietists/${data.userId}`,
                        { withCredentials: true }
                    );
                    setDietist(profileRes.data);
                }
                navigate('/home');
            } catch (error) {
                if (error?.response?.status !== 401) {
                    console.error('Auth check error:', error);
                }
            }
        };
        checkAuth();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── Google Identity Services button ── */
    useEffect(() => {
        if (!window.google) return;
        const container = document.getElementById('lp-google-container');
        if (!container) return;
        while (container.firstChild) container.removeChild(container.firstChild);
        window.google.accounts.id.initialize({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            callback: handleGoogleLogin,
        });
        window.google.accounts.id.renderButton(
            container,
            { theme: 'outline', size: 'large', width: 200, locale: currentLanguage }
        );
    }, [currentLanguage]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleGoogleLogin = async (response) => {
        try {
            setLoading(true);
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/auth/dietist/login/google`,
                { token: response.credential },
                { withCredentials: true }
            );
            setDietist(res.data.dietist);
            navigate('/home');
        } catch (error) {
            console.error('Google login failed:', error);
            notification.showError(t('errorMessages.unknown-error'));
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.email)                     newErrors.email    = t('errorMessages.emailRequired');
        else if (!validateEmail(formData.email)) newErrors.email    = t('errorMessages.emailInvalid');
        if (!formData.password)                  newErrors.password = t('errorMessages.password-required');
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            Object.values(newErrors).forEach(msg => notification.showWarning(msg));
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        try {
            setLoading(true);
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/auth/dietist/login`,
                formData,
                { withCredentials: true }
            );
            setDietist(res.data.dietist);
            navigate('/home');
        } catch (error) {
            console.error('Login error:', error);
            const status = error?.response?.status;
            if (status === 401) {
                const msg = t('errorMessages.incorrect-email-or-password');
                setErrors({ password: msg });
                notification.showWarning(msg);
            } else if (status === 403) {
                const msg = t('errorMessages.email-not-confirmed');
                setErrors({ email: msg });
                notification.showWarning(msg);
            } else if (status === 428) {
                const msg = t('errorMessages.password-not-set');
                setErrors({ password: msg });
                notification.showWarning(msg);
            } else if (status === 429) {
                notification.showWarning(t('errorMessages.too-many-requests'));
            } else {
                notification.showError(t('errorMessages.unknown-error'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
        <PublicNavbar />
        <div className="lp">
            <div className="lp-card">

                {/* ── Hero image ── */}
                <div className="lp-hero-wrapper">
                    <img
                        className="lp-hero-image"
                        src="/images/salad.jpg"
                        alt="Healthy fresh salad"
                    />
                </div>

                {/* ── Header ── */}
                <div className="lp-header">
                    <h1 className="lp-title">{t('lp-title')}</h1>
                    <p className="lp-subtitle">{t('lp-subtitle')}</p>
                </div>

                {/* ── Form ── */}
                <form className="lp-form" onSubmit={handleSubmit} noValidate>

                    <FormField
                        label={t('lp-email-label')}
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder={t('lp-email-placeholder')}
                        icon="✉️"
                        error={errors.email}
                        maxLength={254}
                        autoComplete="email"
                    />

                    <FormField
                        label={t('lp-password-label')}
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder={t('lp-password-placeholder')}
                        icon="🔒"
                        error={errors.password}
                        maxLength={128}
                        autoComplete="current-password"
                    />

                    {/* Remember me + Forgot password */}
                    <div className="lp-meta-row">
                        <label className="lp-checkbox-label">
                            <input
                                type="checkbox"
                                name="keepLogin"
                                checked={formData.keepLogin}
                                onChange={handleInputChange}
                            />
                            {t('lp-remember-me')}
                        </label>
                        <Link className="lp-forgot-link" to="/forgot-password">
                            {t('lp-forgot-password')}
                        </Link>
                    </div>

                    {/* Sign In button */}
                    <FormButton
                        type="submit"
                        fullWidth
                        loading={loading}
                        loadingText={t('sending')}
                    >
                        {t('lp-sign-in-btn')}
                    </FormButton>

                    {/* OR divider */}
                    <div className="lp-divider">
                        <span>{t('or')}</span>
                    </div>

                    {/* Google Sign In */}
                    <div className="lp-google-wrapper">
                        <div id="lp-google-container" />
                    </div>

                    {/* Sign-up prompt */}
                    <div className="lp-footer">
                        <p>
                            {t('lp-no-account')}
                            <Link to="/register">{t('lp-sign-up')}</Link>
                        </p>
                    </div>

                </form>

            </div>
        </div>
        </>
    );
};

export default LoginPage;
